import { IonContent, IonIcon } from '@ionic/react';
import {
  barbell,
  boat,
  colorPalette,
  flame,
  gameController,
  heart,
  lockClosed,
  mic,
  musicalNotes,
  person,
  restaurant,
} from 'ionicons/icons';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { useAccount } from '@/hooks/useAccount.ts';
import { useCreateStripeClient } from '@/hooks/useCreateStripeClient.ts';
import { OnboardingFooter } from '@/pages/Onboarding/components/Footer';
import { useTransLayoutSelections } from '@/pages/Onboarding/hooks/useTransLayoutSelections';
import { LayoutDataType } from '@/pages/Onboarding/Layouts/OwnAIMan/types.ts';
import { userService } from '@/services/user';
import { trackOnboardingComplete } from '@/utils/analytics';

import { getImageBySelection } from '../../utils/getImageBySelection';
import { getNameByHisType } from '../../utils/getNameByHisType';
import { getTagsBySelection } from '../../utils/getTagsBySelection';

import {
  BackgroundWrapper,
  Badge,
  Badges,
  ContentWrapper,
  Main,
  PageStyles,
  Subtitle,
  Tag,
  Tags,
  Title,
} from './SummaryScreen.styles';

const iconMap: Record<string, string> = {
  person,
  mic,
  heart,
  barbell,
  'color-palette': colorPalette,
  'musical-notes': musicalNotes,
  'game-controller': gameController,
  restaurant,
  boat,
};

export const SummaryScreen: React.FC = () => {
  const { refreshUser } = useAccount();
  const { createStripeClient, isCreatingStripeClient } =
    useCreateStripeClient();

  const selections = useTransLayoutSelections();
  const tags = getTagsBySelection(selections);
  const image = getImageBySelection(selections);
  const name = getNameByHisType(selections.his_type);

  const form = useFormContext<LayoutDataType>();

  const formEmail = form.watch('email');

  const handleNextStep = async (): Promise<void> => {
    try {
      const lowerCaseEmail = formEmail.toLowerCase().trim();

      trackOnboardingComplete({ email: lowerCaseEmail });

      await createStripeClient(lowerCaseEmail);
      await userService.createOrUpdate({
        onboarding_data: {
          onboarded: true,
        },
      });

      refreshUser();
    } catch (error) {
      console.error('Can not create a stripe user', error);
    }
  };

  return (
    <PageStyles background="purple-gradient">
      <BackgroundWrapper backgroundImage={image}>
        <IonContent className="summary-screen-content">
          <ContentWrapper>
            <Main>
              <Badges>
                <Badge variant="private">
                  <IonIcon icon={lockClosed} /> Private
                </Badge>
                <Badge variant="fire">
                  <IonIcon icon={flame} /> <IonIcon icon={flame} />{' '}
                  <IonIcon icon={flame} />
                </Badge>
              </Badges>
              <Title>{name}</Title>
              <Subtitle>Matched all your preferences</Subtitle>
              <Tags>
                {tags.map((tag, idx) => (
                  <Tag key={idx}>
                    <IonIcon icon={iconMap[tag.icon]} /> {tag.label}
                  </Tag>
                ))}
              </Tags>
            </Main>
          </ContentWrapper>
        </IonContent>
        <OnboardingFooter
          onContinue={handleNextStep}
          buttonColor={'purple-gradient'}
          buttonRadius={'sm'}
          buttonSize={'large'}
          disabled={isCreatingStripeClient}
          buttonText="Start chatting"
        />
      </BackgroundWrapper>
    </PageStyles>
  );
};
